import { DirectiveOptions } from 'vue';
import {
  applyValidationMessage, FieldValidator, isValidatableField, ValidatableField,
} from '@/helpers/formValidation';

/**
 * Translated messages and a red border for native (HTML5) form validation.
 *
 * The forms rely on `required` / `pattern` + `form.reportValidity()`, so the
 * browser shows its own "Please fill out this field." in the OS language. This
 * directive hooks the `invalid` event the browser fires for every failing field
 * and swaps that text for the site's translation (which one is decided in
 * `helpers/formValidation.ts`), using the same Constraint Validation API
 * (`setCustomValidity`) - nothing about how the form is submitted changes.
 *
 * A field that failed gets Bootstrap's `is-invalid` class (red border) and keeps
 * it until the user fixes it: every `input` / `change` re-checks the field and
 * toggles the class to match. A field is only ever re-checked after it failed
 * once, so a field the user is still filling in for the first time is not
 * flagged prematurely. `reset` clears everything.
 *
 * Rules the browser has no attribute for (matching passwords, a widget whose
 * real value lives in Vue state - vue-multiselect) are passed as the directive's
 * value, a `FieldValidator`. Its answer is stored with `setCustomValidity`, which
 * is what makes `reportValidity()` stop on that field like on any other. The red
 * border still only appears once `reportValidity()` has run.
 *
 * The rule is re-evaluated for EVERY field of the form on every edit and on
 * every re-render of the owning component: it usually depends on other fields
 * (the second password on the first), and a widget may change its value without
 * any native event at all (picking an option in vue-multiselect only touches
 * Vue state, which re-renders the form).
 *
 * Usage: `<b-form ref="form" v-native-validation>` or
 * `<b-form ref="form" v-native-validation="rule">` - on the <form> itself.
 * Any field inside is covered, including ones rendered later by `v-if`.
 */

const INVALID_CLASS = 'is-invalid';

type Teardown = () => void;

const teardowns = new WeakMap<HTMLElement, Teardown>();

const validators = new WeakMap<HTMLElement, FieldValidator | undefined>();

// Fields that failed at least once. Kept here rather than read back from the
// class: Vue may drop a class it did not render itself on the next patch.
const validated = new WeakSet<ValidatableField>();

function markInvalid(field: ValidatableField, validator?: FieldValidator) {
  validated.add(field);
  applyValidationMessage(field, validator);
  field.classList.add(INVALID_CLASS);
}

function recheck(field: ValidatableField, validator?: FieldValidator) {
  applyValidationMessage(field, validator);
  field.classList.toggle(INVALID_CLASS, !field.validity.valid);
}

function asValidator(value: unknown): FieldValidator | undefined {
  return typeof value === 'function' ? value as FieldValidator : undefined;
}

function fields(form: HTMLElement): ValidatableField[] {
  return Array.from(form.querySelectorAll<ValidatableField>('input, select, textarea'));
}

function sync(form: HTMLElement) {
  const validator = validators.get(form);
  fields(form).forEach((field) => {
    if (validated.has(field)) {
      recheck(field, validator);
    } else if (validator) {
      // Not flagged yet - only keep the form's own rule in sync so that the
      // next reportValidity() sees it; the border waits for that call
      field.setCustomValidity(validator(field));
    }
  });
}

const nativeValidation: DirectiveOptions = {
  bind(el, binding) {
    validators.set(el, asValidator(binding.value));

    const onInvalid = (event: Event) => {
      if (isValidatableField(event.target)) markInvalid(event.target, validators.get(el));
    };
    const onEdit = () => sync(el);
    const onReset = () => {
      fields(el).forEach((field) => {
        validated.delete(field);
        field.setCustomValidity('');
        field.classList.remove(INVALID_CLASS);
      });
    };

    // `invalid` does not bubble - only the capture phase sees it at the form
    el.addEventListener('invalid', onInvalid, true);
    el.addEventListener('input', onEdit);
    el.addEventListener('change', onEdit);
    el.addEventListener('reset', onReset);

    teardowns.set(el, () => {
      el.removeEventListener('invalid', onInvalid, true);
      el.removeEventListener('input', onEdit);
      el.removeEventListener('change', onEdit);
      el.removeEventListener('reset', onReset);
    });
  },

  inserted(el) {
    sync(el);
  },

  update(el, binding) {
    validators.set(el, asValidator(binding.value));
  },

  componentUpdated(el) {
    sync(el);
  },

  unbind(el) {
    const teardown = teardowns.get(el);
    if (teardown) teardown();
    teardowns.delete(el);
    validators.delete(el);
  },
};

export default nativeValidation;
